import { Alert } from '@/components/ui/alert';
import { Avatar, brandAvatarTint } from '@/components/ui/avatar';
import {
  Box,
  Button,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import type {
  McpCopublicityApprovalRequest,
  McpCopublicityReviewResponse,
  McpCopublicityOperationResponse,
} from '@fluenceur/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { McpClientLogo } from './McpClientLogo';
import { McpCopublicityContent } from './McpCopublicityContent';

/** `spokenValue` replaces the visible value for assistive technology, e.g. a masked identifier. */
function Detail({
  label,
  value,
  spokenValue,
}: {
  label: string;
  value: string | number;
  spokenValue?: string;
}) {
  return (
    <Box>
      <Text color="fg.muted" fontSize="sm">
        {label}
      </Text>
      <Text overflowWrap="anywhere">
        {spokenValue ? (
          <>
            <span aria-hidden="true">{value}</span>
            <VisuallyHidden>{spokenValue}</VisuallyHidden>
          </>
        ) : (
          value
        )}
      </Text>
    </Box>
  );
}
const MASK = '••••';
export function McpCopublicityReview({
  review,
  operation,
  onApprove,
  onRefresh,
}: {
  review: McpCopublicityReviewResponse;
  operation?: McpCopublicityOperationResponse;
  onApprove: (request: McpCopublicityApprovalRequest) => Promise<unknown>;
  onRefresh: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState(false);
  const [changed, setChanged] = useState(false);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const submitting = useRef(false);
  const key = useRef(crypto.randomUUID());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const expired =
    !Number.isFinite(Date.parse(review.preparation.expiresAt)) ||
    now >= Date.parse(review.preparation.expiresAt);
  const terms = review.launchReview.ok
    ? review.launchReview.configSnapshot
    : review.preparation.payload.review;
  // Four characters or fewer would be the whole identifier: show none of it.
  const accountEnding =
    terms.adAccountId && terms.adAccountId.length > 4 ? terms.adAccountId.slice(-4) : null;
  const money = (cents: number, currency: string) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(cents / 100);
  let hostname: string | null = null;
  if (review.client.registrationMethod === 'cimd') {
    try {
      hostname = new URL(review.client.id).hostname;
    } catch {
      /* Server validates client identifiers. */
    }
  }
  const stop = review.preparation.payload.action === 'stop';
  // A launch pays to show one post: with no handle, no cover and no link, the brand cannot tell
  // which. Stopping a live boost only ever spends less, so it is never held back.
  const unidentified =
    !stop &&
    !review.content.creatorHandle &&
    !review.content.thumbnailUrl &&
    !review.content.permalink;
  const blocked =
    changed ||
    expired ||
    unidentified ||
    !review.launchReview.ok ||
    review.preparation.status !== 'draft';
  const approve = async () => {
    // `launchReview.ok` again so the type narrows to the reviewed terms hash below.
    if (submitting.current || blocked || !review.launchReview.ok) return;
    submitting.current = true;
    setPending(true);
    setFailed(false);
    try {
      await onApprove({
        expectedVersion: review.preparation.version,
        payloadHash: review.preparation.payloadHash,
        termsHash: review.launchReview.termsHash,
        idempotencyKey: key.current,
      });
    } catch (error) {
      if (
        (error as { response?: { data?: { code?: string } } }).response?.data?.code ===
        'E_MCP_REVIEW_CHANGED'
      )
        setChanged(true);
      else setFailed(true);
      submitting.current = false;
      setPending(false);
    }
  };
  return (
    <Stack gap={6}>
      <HStack gap={3}>
        <Avatar
          src={review.brand.imageUrl || undefined}
          name={review.brand.name}
          {...brandAvatarTint}
          size="md"
        />
        <Box>
          <Heading size="xl">
            {t(stop ? 'mcp.copublicity.stopTitle' : 'mcp.copublicity.title')}
          </Heading>
          <Text>{review.brand.name}</Text>
        </Box>
      </HStack>
      <HStack gap={3}>
        <McpClientLogo provider={review.client.provider} />
        <Box>
          <Text fontWeight="semibold">{review.client.name}</Text>
          {hostname && (
            <Text color="fg.muted" fontSize="sm">
              {hostname}
            </Text>
          )}
        </Box>
      </HStack>
      <McpCopublicityContent content={review.content} />
      <Text color="fg.muted">{t('mcp.copublicity.subtitle')}</Text>
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
        <Detail
          label={t('mcp.copublicity.budget')}
          value={money(terms.budgetAmountInCents, terms.budgetCurrencyCode)}
        />
        <Detail
          label={t('mcp.copublicity.duration')}
          value={t('mcp.copublicity.days', { count: terms.durationDays })}
        />
        <Detail
          label={t('mcp.copublicity.account')}
          value={
            terms.adAccountId ? `${MASK}${accountEnding ?? ''}` : t('common.unavailable')
          }
          spokenValue={
            !terms.adAccountId
              ? undefined
              : accountEnding
                ? t('mcp.copublicity.accountEnding', { last4: accountEnding })
                : t('mcp.copublicity.accountMasked')
          }
        />
        <Detail
          label={t('mcp.copublicity.destination')}
          value={terms.targetUrl ?? terms.product?.title ?? t('common.unavailable')}
        />
        <Detail
          label={t('mcp.copublicity.audience')}
          value={t(`mcp.copublicity.audienceModes.${terms.audienceMode}`, {
            defaultValue: terms.audienceMode,
          })}
        />
        <Detail
          label={t('mcp.copublicity.locations')}
          value={
            terms.targetLocations.map((location) => location.name).join(', ') ||
            t('mcp.copublicity.noLocations')
          }
        />
        <Detail
          label={t('mcp.copublicity.age')}
          value={`${terms.targetAgeRange.min}–${terms.targetAgeRange.max}`}
        />
        <Detail
          label={t('mcp.copublicity.campaign')}
          value={terms.parentCampaignName ?? t('mcp.copublicity.newCampaign')}
        />
      </SimpleGrid>
      {terms.campaignBudgetManaged && (
        <Alert status="info" title={t('mcp.copublicity.sharedBudget')} />
      )}
      {terms.utmParameters?.length ? (
        <Box>
          <Text fontWeight="semibold">{t('mcp.copublicity.tracking')}</Text>
          {terms.utmParameters.map((pair) => (
            <Text key={pair.key} overflowWrap="anywhere">
              {pair.key}: {pair.value}
            </Text>
          ))}
        </Box>
      ) : null}
      {terms.displayRights ? (
        <Box>
          <Text fontWeight="semibold">{t('mcp.copublicity.rights')}</Text>
          <Text>
            {money(terms.displayRights.amountCents, terms.displayRights.currencyCode)} ·{' '}
            {t(`mcp.copublicity.compensationModels.${terms.displayRights.compensationModel}`)}
          </Text>
          <Text>
            {t('mcp.copublicity.rightsTerms', {
              months: terms.displayRights.durationMonths,
              scope: terms.displayRights.scope,
              version: terms.displayRights.versionNumber,
            })}
          </Text>
        </Box>
      ) : (
        <Text color="fg.muted">{t('mcp.copublicity.noCompensation')}</Text>
      )}
      {review.operationId ? (
        <Alert
          status="info"
          title={t(`mcp.copublicity.status.${operation?.status ?? 'accepted'}`, {
            defaultValue: t('mcp.copublicity.accepted'),
          })}
        >
          {operation?.deliveryState && (
            <Text>{t(`mcp.copublicity.delivery.${operation.deliveryState}`)}</Text>
          )}
          {operation?.reason && (
            <Text>
              {t(`mcp.copublicity.reasons.${operation.reason}`, {
                defaultValue: t('mcp.copublicity.operationAttention'),
              })}
            </Text>
          )}
        </Alert>
      ) : (
        <>
          {!review.launchReview.ok && (
            <Alert
              status="warning"
              title={t(`mcp.copublicity.blockers.${review.launchReview.error}`, {
                defaultValue: t('mcp.copublicity.blocked'),
              })}
            />
          )}
          {expired && <Alert status="warning" title={t('mcp.copublicity.expired')} />}
          {unidentified && <Alert status="warning" title={t('mcp.copublicity.identityMissing')} />}
          {changed && <Alert status="warning" title={t('mcp.copublicity.reviewChanged')} />}
          {failed && <Alert status="error" title={t('mcp.copublicity.approvalFailed')} />}
          <Button
            colorPalette={stop ? 'red' : 'green'}
            disabled={pending || blocked}
            loading={pending}
            onClick={() => void approve()}
          >
            {t(stop ? 'mcp.copublicity.confirmStop' : 'mcp.copublicity.confirmLaunch')}
          </Button>
        </>
      )}
      <Button variant="outline" disabled={pending} onClick={onRefresh}>
        {t('mcp.copublicity.refresh')}
      </Button>
    </Stack>
  );
}
